/**
 * Entry point. Boots the shell, registers scene factories as lazy dynamic
 * imports (so the Hub paints before Game 1/2 code is even fetched), and exposes
 * a small debug handle used by the automated capture harness.
 */
import { App } from './core/app';
import { setAcademyProgressAccount } from './core/academyProgress';
import type { RouteId, SceneFactory } from './core/app';
import { AuthClient } from './auth/client';
import { showLoginPage } from './auth/loginPage';
import { AuthError } from './auth/types';
import { showTeacherCms } from './cms/teacherPage';
import { GameProgressReporter } from './cms/gameProgress';
import { installCssVars } from './render/palette';
import { el } from './ui/dom';

const canvas = document.querySelector<HTMLCanvasElement>('#gl');
const hudRoot = document.querySelector<HTMLElement>('#hud');
const stage = document.querySelector<HTMLElement>('#stage');
const authRoot = document.querySelector<HTMLElement>('#auth-root');
const cmsRoot = document.querySelector<HTMLElement>('#cms-root');
const overlayRoot = document.querySelector<HTMLElement>('#overlay');

if (!canvas || !hudRoot || !stage || !authRoot || !cmsRoot || !overlayRoot) {
  throw new Error('Boot failed: required stage elements are missing from index.html');
}

installCssVars();

const bootLabel = el('div', { text: 'Checking account…' });
const boot = el('div', { id: 'boot' }, bootLabel);
stage.append(boot);

function gameFactories(onStudentLogout?: () => void): Record<RouteId, SceneFactory> {
  return {
    welcome: async () => new (await import('./scenes/academy/AcademyMessageScene')).AcademyMessageScene('welcome'),
    hub: async () => {
      const { HubScene } = await import('./scenes/hub/HubScene');
      return new HubScene(onStudentLogout ? { onLogout: onStudentLogout } : {});
    },
    game1: async () => new (await import('./scenes/game1/Game1Scene')).Game1Scene(),
    game2: async () =>
      new (await import('./scenes/game2/MaterialWorkshopScene')).MaterialWorkshopScene(),
    game3: async () => new (await import('./scenes/game3/Game3Scene')).Game3Scene(),
    'academy-ending': async () => new (await import('./scenes/academy/AcademyMessageScene')).AcademyMessageScene('academy-ending'),
  };
}

function staffGameMode(): boolean {
  return new URLSearchParams(window.location.search).get('play') === '1';
}

function installStaffGameBar(
  displayName: string,
  auth: AuthClient
): void {
  const openCms = (): void => {
    const url = new URL(window.location.href);
    url.searchParams.delete('play');
    url.hash = '/cms/dashboard';
    window.location.assign(url.toString());
  };
  overlayRoot!.append(
    el(
      'nav',
      { class: 'staff-game-bar', attrs: { 'aria-label': 'Staff game controls' } },
      el('span', { text: `Playing as ${displayName}` }),
      el('button', {
        class: 'btn btn--confirm',
        text: 'Open CMS',
        attrs: { type: 'button' },
        on: { click: openCms },
      }),
      el('button', {
        class: 'btn',
        text: 'Sign out',
        attrs: { type: 'button' },
        on: {
          click: () =>
            void auth.signOut().finally(() => {
              window.location.reload();
            }),
        },
      })
    )
  );
}

async function main(): Promise<void> {
  const auth = new AuthClient();
  let session;
  let initialError: 'service_unavailable' | undefined;
  try {
    session = await auth.session();
  } catch (reason) {
    initialError =
      reason instanceof AuthError && reason.code !== 'service_unavailable'
        ? undefined
        : 'service_unavailable';
  }

  if (!session) {
    stage!.hidden = true;
    session = await showLoginPage(authRoot!, auth, { initialError });
    stage!.hidden = false;
  }

  document.documentElement.setAttribute('data-authenticated', 'true');
  document.documentElement.setAttribute('data-account-role', session.user.role);

  const isStaff = session.user.role === 'teacher' || session.user.role === 'admin';
  if (isStaff && !staffGameMode()) {
    stage!.hidden = true;
    boot.remove();
    await showTeacherCms(cmsRoot!, session, auth);
    (window as unknown as Record<string, unknown>).__cca = {
      account: {
        displayName: session.user.displayName,
        role: session.user.role,
        schoolId: session.user.schoolId,
      },
      logout: async () => {
        await auth.signOut();
        window.location.reload();
      },
      ready: true,
      route: () => 'cms',
    };
    return;
  }

  bootLabel.textContent = 'Loading Academy…';
  setAcademyProgressAccount(session.user.id);

  // Fail loudly and legibly rather than showing a black rectangle.
  const gl = canvas!.getContext('webgl2') ?? canvas!.getContext('webgl');
  if (!gl) {
    boot.replaceChildren(
      el('div', { text: 'This experience needs WebGL, which your browser has disabled.' })
    );
    return;
  }

  let progress: GameProgressReporter | undefined;
  const onStudentLogout =
    session.user.role === 'student'
      ? () => {
          void auth.signOut().finally(() => window.location.reload());
        }
      : undefined;
  const app = new App(canvas!, hudRoot!, gameFactories(onStudentLogout), {
    entered: (route) => progress?.entered(route),
    exiting: (route) => progress?.exiting(route),
  });
  if (session.user.role === 'student') progress = new GameProgressReporter(app.session);
  if (isStaff) installStaffGameBar(session.user.displayName, auth);
  await app.start();

  boot.classList.add('hidden');
  setTimeout(() => boot.remove(), 500);

  // Handle used by tools/capture.mjs to know when a scene is truly ready.
  (window as unknown as Record<string, unknown>).__cca = {
    app,
    account: {
      displayName: session.user.displayName,
      role: session.user.role,
      schoolId: session.user.schoolId,
    },
    logout: async () => {
      progress?.dispose();
      await auth.signOut();
      window.location.hash = '/login';
      window.location.reload();
    },
    ready: true,
    stats: () => app.engine.stats,
    route: () => document.documentElement.getAttribute('data-route'),
  };

  // `?debug=forge` deep-links the procedural inspection sheet — every baked
  // texture as a tiling swatch and every forged geometry cel-shaded on a turntable
  // — matching the `?debug=outline` hatch the engine already exposes. Imported
  // lazily so the forge never sits in the hub's critical path; `window.__forgeDemo()`
  // remains available on every route via the stub that `render/textures` installs.
  if (new URLSearchParams(window.location.search).get('debug') === 'forge') {
    (await import('./render/geometry')).forgeDemo();
  }
}

void main().catch((err) => {
  console.error(err);
  boot.replaceChildren(el('div', { text: `Boot error: ${String(err)}` }));
});
